import { toDate } from '../../../shared/utils/dateHelpers';
import { NominationTrainingSnapshot } from '../nomination.types';

interface VisitHistoryLike {
  session_date?: unknown;
  no_show?: unknown;
  late_cancel?: unknown;
}

export interface TrainingHistorySource {
  visit_dates?: unknown;
  visit_history?: unknown;
  first_session_date?: unknown;
}

const EMPTY_SNAPSHOT: NominationTrainingSnapshot = {
  sessionsCompleted: 0,
  totalVisits: 0,
  noShows: 0,
  lateCancels: 0,
  firstSessionDate: null,
  latestSessionDate: null,
};

function toIsoOrNull(value: unknown): string | null {
  const date = toDate(value);
  return date ? date.toISOString() : null;
}

function sortedIsoDates(values: unknown[]): string[] {
  return values
    .map(toDate)
    .filter((date): date is Date => date !== null)
    .sort((a, b) => a.getTime() - b.getTime())
    .map(date => date.toISOString());
}

/**
 * Builds the training-history snapshot stored on a nomination.
 *
 * "Sessions completed" excludes no-shows and late cancellations when the
 * detailed `visit_history` is available (matching the Sessions tab), and falls
 * back to the raw `visit_dates` count for cache documents without history.
 */
export function buildTrainingSnapshot(client: TrainingHistorySource | null | undefined): NominationTrainingSnapshot {
  if (!client) return { ...EMPTY_SNAPSHOT };

  const history = Array.isArray(client.visit_history)
    ? (client.visit_history as VisitHistoryLike[]).filter(entry => entry && typeof entry === 'object')
    : [];
  const visitDates = Array.isArray(client.visit_dates) ? client.visit_dates : [];

  let sessionsCompleted = 0;
  let noShows = 0;
  let lateCancels = 0;
  let totalVisits = 0;
  let orderedDates: string[] = [];

  if (history.length > 0) {
    totalVisits = history.length;
    history.forEach(entry => {
      if (entry.no_show === true) {
        noShows += 1;
      } else if (entry.late_cancel === true) {
        lateCancels += 1;
      } else {
        sessionsCompleted += 1;
      }
    });
    orderedDates = sortedIsoDates(history.map(entry => entry.session_date));
  } else {
    totalVisits = visitDates.length;
    sessionsCompleted = visitDates.length;
    orderedDates = sortedIsoDates(visitDates);
  }

  const firstSessionDate = toIsoOrNull(client.first_session_date) || orderedDates[0] || null;
  const latestSessionDate = orderedDates.length > 0 ? orderedDates[orderedDates.length - 1] : null;

  return {
    sessionsCompleted,
    totalVisits,
    noShows,
    lateCancels,
    firstSessionDate,
    latestSessionDate,
  };
}

/** Human-friendly label for a sessions count, e.g. "1 session" / "12 sessions". */
export function formatSessionsLabel(count: number): string {
  return `${count} ${count === 1 ? 'session' : 'sessions'}`;
}
